import { FC } from 'react';

// Third-party
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import Icon from '@mdi/react';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material';

// Components
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { MHidden } from '@mgm/common/components/responsive';
import { BaseFilterMenu } from '../../../../components/base';
import { ToolbarWrapper } from '../../Shared/components';

// Hooks
import { useAppSelector } from '../../../../hooks/useAppStore';
import { usePrint } from '../../../../hooks/usePrint';
import { useScheduleFilters } from '../hooks';
import { useFilter } from '../../../../hooks/useFilter';

// Redux-toolkit

// Data
import { SCHEDULE_OPTIONS } from '../../../../data';

// Types
import { TTableMainHeader } from '../type';

// Assets
import appLogo from '../../../../assets/appLogo.png';
import { getIsPrintMode } from '../../../../store/common/selectors';

const TableMainHeader: FC<TTableMainHeader> = ({
  onToday,
  onNextDate,
  onPrevDate,
  date,
  printRef,
}) => {
  const { t } = useTranslation(['schedule', 'inputs']);
  const theme = useTheme() as TCustomTheme;
  const { isLight, isMobile } = useCustomTheme(theme);
  const { schedulesFilterItems } = useScheduleFilters();
  const { filterItems } = useFilter(schedulesFilterItems);
  const { handlePrint } = usePrint(printRef);
  const isPrintMode = useAppSelector(getIsPrintMode);

  const month = moment(date).format('MMMM')?.toLowerCase();

  const mapValueToActions: { [key: string]: () => void } = {
    print: handlePrint,
  };

  return (
    <ToolbarWrapper>
      <MHidden width="smDown">
        {!isPrintMode && (
        <Stack direction="row" spacing={1.5}>
          {SCHEDULE_OPTIONS.map((viewOption) => (
            <ToggleButton
              key={viewOption.value}
              component="div"
              value={viewOption.value}
              sx={{ width: 32, height: 32, padding: 0 }}
            >
              {viewOption.value !== 'filter' ? (
                <SvgIcon
                  onClick={mapValueToActions[viewOption.value]}
                  component={viewOption.icon}
                  inheritViewBox
                  fontSize="small"
                />
              ) : <BaseFilterMenu isDisplayLabel={false} items={filterItems} />}
            </ToggleButton>
          ))}
          <Stack pl={2} direction="row" gap={1}>
            <Stack spacing={2} direction="row" alignItems="center">
              <Typography variant="body2">{t('salary', { ns: 'inputs' })}</Typography>
              <Box sx={{ bgcolor: '#7954A1', width: 40, height: 20 }} />
            </Stack>
            <Stack spacing={2} direction="row" alignItems="center">
              <Typography variant="body2">{t('hourly', { ns: 'inputs' })}</Typography>
              <Box sx={{ bgcolor: 'rgba(168, 85, 247, 0.16)', width: 40, height: 20 }} />
            </Stack>
            <Stack spacing={2} direction="row" alignItems="center">
              <Typography variant="body2">{t('timeoff')}</Typography>
              <Box sx={{ bgcolor: '#DFE3E8', width: 40, height: 20 }} />
            </Stack>
          </Stack>
        </Stack>
        )}
      </MHidden>

      <Box
        {...(isMobile && {
          justifyContent: 'space-between',
          pt: 2,
          pb: 2,
          pl: 2,
        })}
        display={isMobile ? 'flex' : 'contents'}
      >
        {isPrintMode
          ? <img src={appLogo} width={70} height={70} alt="appLogo" />
          : (
            <Typography
              variant="h5"
              sx={{
                my: { xs: 1, sm: 0 },
                color: isLight ? theme.palette.grey[900] : theme.palette.grey[100],
              }}
            >
              {t(`${month}`)}
              {' '}
              {moment(date).format('yyyy')}
            </Typography>
          )}

        {!isPrintMode && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onPrevDate}>
            <Icon path={mdiChevronLeft} size={0.8} />
          </IconButton>

          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={onToday}
            sx={{ mx: 0.5 }}
          >
            {t('today')}
          </Button>

          <IconButton onClick={onNextDate}>
            <Icon path={mdiChevronRight} size={0.8} />
          </IconButton>
        </Box>
        )}
      </Box>
    </ToolbarWrapper>
  );
};

export {
  TableMainHeader,
};
